import { useState, useEffect, type ReactNode } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Pencil, X, Loader2 } from "lucide-react";
import {
  useCreateAiPromptVersion,
  useDeleteAiPromptVersion,
} from "@/resources/ai-prompt";
import type { AiPrompt, AiPromptVersion, PayloadToken } from "@/types/ai-prompt";
import { handleApiError } from "@/utils";
import { getActiveVersionText } from "./utils";
import { PromptTokenBadges } from "./PromptTokenBadges";
import { VersionDiffModal } from "./VersionDiffModal";
import { VersionHistoryList } from "./VersionHistoryList";

type PromptEditorPaneProps = {
  prompt: AiPrompt;
  featureLabel: string;
  payloadTokens: PayloadToken[];
  onClose: () => void;
  /** Rendered between the prompt body editor and the version history (e.g. the per-prompt validator checks). */
  afterBody?: ReactNode;
};

export function PromptEditorPane({ prompt, featureLabel, payloadTokens, onClose, afterBody }: PromptEditorPaneProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [draftBody, setDraftBody] = useState("");
  const [diffVersion, setDiffVersion] = useState<AiPromptVersion | null>(null);

  const { mutate: createVersion, isPending: isCreating } = useCreateAiPromptVersion(prompt.id);
  const { mutate: deleteVersion } = useDeleteAiPromptVersion();

  const activeVersionText = getActiveVersionText(prompt);
  const activeVersion = prompt.versions.find((v) => v.is_active) ?? null;

  useEffect(() => {
    if (isEditing) {
      setDraftBody(activeVersionText);
    }
  }, [isEditing, activeVersionText]);

  const handleStartEdit = () => {
    setIsEditing(true);
  };

  const handleSavePrompt = () => {
    const autoName = `Edited ${new Date().toLocaleString()}`;
    createVersion(
      {
        name: autoName,
        prompt_text: draftBody,
        description: undefined,
      },
      {
        onSuccess: () => {
          toast.success("Prompt saved", { description: `"${prompt.label}" is now live.` });
          setIsEditing(false);
        },
        onError: (error) => handleApiError(error),
      }
    );
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  const handleDeleteVersion = (version: AiPromptVersion) => {
    deleteVersion(
      { promptId: prompt.id, versionId: version.id },
      {
        onSuccess: () => {
          toast.success("Version deleted", { description: `"${version.name}" has been removed.` });
        },
        onError: (error) => handleApiError(error),
      }
    );
  };

  const sortedVersions = [...prompt.versions].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

  return (
    <div className="space-y-6 min-w-0 lg:pl-6 pt-6 lg:pt-0">
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {featureLabel}
            </p>
            <h3 className="text-xl font-semibold text-foreground">
              {prompt.label}
            </h3>
            {prompt.trigger_label && (
              <p className="text-sm text-muted-foreground">
                <span className="font-medium">Trigger:</span>{" "}
                <span className="text-foreground">{prompt.trigger_label}</span>
              </p>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0 -mr-2 -mt-1 h-8 w-8"
            onClick={onClose}
            aria-label="Close panel"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <PromptTokenBadges promptText={activeVersionText} payloadTokens={payloadTokens} />

        <div className="space-y-1.5">
          <div className="flex items-end justify-between gap-3">
            <Label htmlFor="prompt-body" className="text-xs font-medium text-muted-foreground">
              Prompt
            </Label>
            {isEditing ? (
              <div className="flex items-center gap-2 shrink-0">
                <Button variant="outline" size="sm" onClick={handleCancelEdit}>
                  Cancel
                </Button>
                <Button size="sm" onClick={handleSavePrompt} disabled={isCreating}>
                  {isCreating && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                  Save & deploy
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2 shrink-0">
                <Button size="sm" variant="outline" className="shrink-0 gap-1.5" onClick={handleStartEdit}>
                  <Pencil className="h-3.5 w-3.5" />
                  Edit
                </Button>
              </div>
            )}
          </div>

          {isEditing ? (
            <Textarea
              id="prompt-body"
              key={`body-${prompt.id}`}
              value={draftBody}
              onChange={(e) => setDraftBody(e.target.value)}
              rows={18}
              className="rounded-lg bg-card text-sm leading-relaxed font-sans whitespace-pre-wrap"
            />
          ) : (
            <div className="rounded-lg border border-border bg-card p-4">
              <pre className="text-sm leading-relaxed text-muted-foreground whitespace-pre-wrap font-sans m-0">
                {activeVersionText}
              </pre>
            </div>
          )}
        </div>
      </div>

      {afterBody}

      <div className="border-t border-border" />

      <VersionHistoryList
        activeVersion={activeVersion}
        sortedVersions={sortedVersions}
        onViewDiff={setDiffVersion}
        onDeleteVersion={handleDeleteVersion}
      />

      <VersionDiffModal
        open={diffVersion !== null}
        onClose={() => setDiffVersion(null)}
        activeVersionText={activeVersionText}
        pastVersion={diffVersion}
        promptLabel={prompt.label}
        promptId={prompt.id}
      />
    </div>
  );
}
